import { useAuth } from '../contexts/AuthContext';

// Actions hard-blocked on trial (must match backend action slugs)
const TRIAL_RESTRICTED_ACTIONS = new Set([
    'domain_registration',
    'phone_provisioning',
    'wordpress_provision',
]);

export function usePermissions() {
    const { user } = useAuth();

    const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
    const isSuperAdmin = user?.role === 'superadmin';

    const plan: string = (user as any)?.plan ?? 'trial';
    const isTrialUser = plan === 'trial' && !isAdmin;

    const isTrialRestricted = (action: string): boolean =>
        isTrialUser && TRIAL_RESTRICTED_ACTIONS.has(action);

    const canAccessModule = (key: string): boolean => {
        if (!user) return false;
        if (isAdmin) return true;
        if (user.allowed_modules === null || user.allowed_modules === undefined) return true;
        return user.allowed_modules.includes(key);
    };

    const canAccessProject = (projectId: string): boolean => {
        if (!user) return false;
        if (isAdmin) return true;
        if (user.allowed_projects === null || user.allowed_projects === undefined) return true;
        return user.allowed_projects.includes(projectId);
    };

    // Explicit opt-in feature flags (e.g. "noise_filter") — deliberately NOT
    // governed by allowed_modules' "null = all allowed" semantics. A flag
    // must be literally present in user.feature_flags; an unrestricted
    // account does not get it for free. Mirrors authorization.require_feature_flag.
    const hasFeatureFlag = (flagKey: string): boolean => {
        if (!user) return false;
        if (isAdmin) return true;
        return (user.feature_flags ?? []).includes(flagKey);
    };

    return { canAccessModule, canAccessProject, hasFeatureFlag, isAdmin, isSuperAdmin, plan, isTrialUser, isTrialRestricted };
}
